// LeagueShell — the standalone shell for the League workspace.
//
// Structure:
//   Desktop (V3-style):  fixed left sidebar with the 5 nav items, main
//                        content area, topbar with a workspace-switcher
//                        pill.
//   Mobile (V4-style):   compact topbar + bottom tab bar with the same
//                        5 items.
//
// Nothing from the Gabruz Universe nav / sidebar leaks in — a
// committee member is fully inside a separate product here. If they
// also have Gabruz Universe access, a "Switch to Gabruz Universe"
// button in the topbar drops them back into the club app.

import React, { useCallback, useEffect, useState } from "react";
import {
  Landmark, ShieldCheck, CalendarDays, Gavel, Megaphone, DollarSign,
  LogOut, ArrowLeftRight, Sun, Moon, X, Menu, ChevronRight
} from "lucide-react";
import { loadState, logout } from "../api";
import { useTheme } from "./Theme";
import { useWorkspace } from "./Workspace";
import { canEnterWorkspace } from "../data";
import { ActiveTeamProvider } from "./ActiveTeam";
import { Loading } from "./primitives";
import { useToast } from "./Toast";

import LeagueCommittee     from "../views/league/LeagueCommittee";
import LeagueTeams         from "../views/league/LeagueTeams";
import LeagueSchedule      from "../views/league/LeagueSchedule";
import LeagueUmpires       from "../views/league/LeagueUmpires";
import LeagueAnnouncements from "../views/league/LeagueAnnouncements";
import LeagueFinances      from "../views/league/LeagueFinances";

const EMPTY_STATE = {
  members: [], groups: [], teams: [], matches: [], availability: [],
  squads: [], announcements: [], transactions: [], notifications: []
};

const NAV = [
  { key: "committee",     label: "Executive Committee", icon: Landmark },
  { key: "teams",         label: "Teams Management",    icon: ShieldCheck },
  { key: "schedule",      label: "Schedule Management", icon: CalendarDays },
  { key: "umpires",       label: "Umpiring Pool",       icon: Gavel },
  { key: "finances",      label: "Finances",            icon: DollarSign },
  { key: "announcements", label: "Announcements",       icon: Megaphone }
];

function renderView(active, { user, state, reload }) {
  switch (active) {
    case "committee":     return <LeagueCommittee     user={user} state={state}/>;
    case "teams":         return <LeagueTeams user={user}/>;
    case "schedule":      return <LeagueSchedule />;
    case "umpires":       return <LeagueUmpires user={user}/>;
    case "finances":      return <LeagueFinances user={user}/>;
    case "announcements": return <LeagueAnnouncements user={user}/>;
    default:              return <LeagueCommittee     user={user} state={state}/>;
  }
}

export default function LeagueShell({ user, setUser, version }) {
  const { theme, toggleTheme } = useTheme();
  const { setWorkspace }       = useWorkspace();
  const toast                  = useToast();
  const isMobile               = version === "v4";

  const [active,    setActive]    = useState("committee");
  const [state,     setState]     = useState(EMPTY_STATE);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const reload = useCallback(async () => {
    setError("");
    try {
      const next = await loadState();
      setState(next);
    } catch (err) {
      if (err.status === 401) { setUser(null); return; }
      setError(err.message || "Failed to load data");
    }
  }, [setUser]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const next = await loadState();
        if (alive) setState(next);
      } catch (err) {
        if (err.status === 401) { setUser(null); return; }
        if (alive) setError(err.message || "Failed to load data");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [setUser]);

  function signOut() {
    logout();
    setUser(null);
  }

  const canSwitchToGabruz = canEnterWorkspace(user, "gabruz");
  function switchToGabruz() {
    if (!canSwitchToGabruz) {
      toast.error("You don't have Gabruz Universe access.");
      return;
    }
    setWorkspace("gabruz");
    toast.success("Switched to Gabruz Universe.");
  }

  const view = renderView(active, { user, state, reload });

  // ─── Mobile V4 shell ─────────────────────────────────────────
  if (isMobile) {
    return (
      <ActiveTeamProvider user={user} teams={state.teams || []}>
        <div className="v4-shell league-shell league-shell--v4">
          <header className="v4-appbar">
            <div className="v4-appbar-brand">
              <Landmark size={16}/>
              <div>
                <small>LEAGUE</small>
                <strong>{NAV.find(n => n.key === active)?.label || "League"}</strong>
              </div>
            </div>
            <div className="v4-appbar-actions">
              {canSwitchToGabruz && (
                <button
                  type="button"
                  className="v4-iconBtn"
                  onClick={switchToGabruz}
                  title="Switch to Gabruz Universe"
                  aria-label="Switch to Gabruz Universe"
                ><ArrowLeftRight size={18}/></button>
              )}
              <button
                type="button"
                className="v4-iconBtn"
                onClick={() => setDrawerOpen(true)}
                aria-label="Open menu"
              ><Menu size={20}/></button>
            </div>
          </header>

          <main className="v4-main">
            {loading ? (
              <div className="empty"><h2>Loading…</h2><p>Fetching league data.</p></div>
            ) : error ? (
              <div className="empty">
                <h2>Couldn't load data</h2>
                <p>{error}</p>
                <button className="primary" onClick={reload}>Retry</button>
              </div>
            ) : view}
          </main>

          <nav className="v4-tabbar league-tabbar" aria-label="Primary">
            {NAV.map(t => {
              const Icon = t.icon;
              const isActive = active === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  className={isActive ? "v4-tab v4-tab--active" : "v4-tab"}
                  onClick={() => setActive(t.key)}
                  aria-current={isActive ? "page" : undefined}
                >
                  <span className="v4-tab-iconWrap"><Icon size={18}/></span>
                  <span>{t.label.replace(" Management", "").replace("Executive ", "")}</span>
                </button>
              );
            })}
          </nav>

          {drawerOpen && (
            <>
              <div className="v4-sheetBackdrop" onClick={() => setDrawerOpen(false)}/>
              <aside className="v4-sheet" aria-label="Menu">
                <div className="v4-sheet-handle" aria-hidden="true"/>
                <div className="v4-sheet-head">
                  <div className="v4-sheet-user">
                    <div className="avatar">{user.avatar}</div>
                    <div>
                      <strong>{user.name}</strong>
                      <small>{user.email}</small>
                    </div>
                  </div>
                  <button type="button" className="v4-iconBtn" onClick={() => setDrawerOpen(false)} aria-label="Close">
                    <X size={18}/>
                  </button>
                </div>
                <ul>
                  {canSwitchToGabruz && (
                    <li>
                      <button type="button" className="v4-sheet-link"
                        onClick={() => { setDrawerOpen(false); switchToGabruz(); }}>
                        <ArrowLeftRight size={16}/>
                        <span>Switch to Gabruz Universe</span>
                      </button>
                    </li>
                  )}
                  <li>
                    <button type="button" className="v4-sheet-link" onClick={toggleTheme}>
                      {theme === "light" ? <Moon size={16}/> : <Sun size={16}/>}
                      <span>Switch to {theme === "light" ? "dark" : "light"} theme</span>
                    </button>
                  </li>
                  <li>
                    <button type="button" className="v4-sheet-link v4-sheet-link--danger" onClick={signOut}>
                      <LogOut size={16}/>
                      <span>Sign out</span>
                    </button>
                  </li>
                </ul>
              </aside>
            </>
          )}
        </div>
      </ActiveTeamProvider>
    );
  }

  // ─── Desktop shell (V3-style) ────────────────────────────────
  return (
    <ActiveTeamProvider user={user} teams={state.teams || []}>
      <div className="league-shell">
        <aside className="league-sidebar">
          <div className="league-sidebar-brand">
            <Landmark size={20}/>
            <div>
              <small>PITTSBURGH</small>
              <strong>Cricket League</strong>
            </div>
          </div>

          <nav className="league-sidebar-nav" aria-label="League">
            {NAV.map(({ key, label, icon: Icon }) => {
              const isActive = active === key;
              return (
                <button
                  key={key}
                  type="button"
                  className={`league-sidebar-item${isActive ? " league-sidebar-item--active" : ""}`}
                  onClick={() => setActive(key)}
                  aria-current={isActive ? "page" : undefined}
                >
                  <Icon size={16}/>
                  <span>{label}</span>
                  {isActive && <ChevronRight size={14}/>}
                </button>
              );
            })}
          </nav>

          <div className="league-sidebar-foot">
            {canSwitchToGabruz && (
              <button
                type="button"
                className="league-sidebar-switch"
                onClick={switchToGabruz}
                title="Switch to Gabruz Universe"
              >
                <ArrowLeftRight size={14}/>
                <span>Switch to Gabruz Universe</span>
              </button>
            )}
            <div className="league-sidebar-user">
              <div className="league-sidebar-user-avatar">{user.avatar}</div>
              <div className="league-sidebar-user-info">
                <strong>{user.name}</strong>
                <small>{user.roles[0]?.replace("_", " ")}</small>
              </div>
              <button
                type="button"
                className="league-sidebar-user-btn"
                onClick={toggleTheme}
                title={theme === "light" ? "Dark mode" : "Light mode"}
                aria-label="Toggle theme"
              >
                {theme === "light" ? <Moon size={14}/> : <Sun size={14}/>}
              </button>
              <button
                type="button"
                className="league-sidebar-user-btn"
                onClick={signOut}
                title="Sign out"
                aria-label="Sign out"
              >
                <LogOut size={14}/>
              </button>
            </div>
          </div>
        </aside>

        <main className="league-main">
          {loading ? (
            <div className="empty">
              <Loading label="Loading league data…" size="lg"/>
            </div>
          ) : error ? (
            <div className="empty">
              <h2>Couldn't load data</h2>
              <p>{error}</p>
              <button className="primary" onClick={reload}>Retry</button>
            </div>
          ) : view}
        </main>
      </div>
    </ActiveTeamProvider>
  );
}
